/*
Copyright 2026 SSH Communications Security

*/

/* Root cause found and patched.

thread.isRelevantToUser() was relying heavily on hasCurrentUserParticipated, which can be stale for non-root participants. That makes thread activity disappear for users who replied but didn’t author the root.
This patch is to fix it directly from the web repo without having to modify matrix-js-sdk */

import { Thread, THREAD_RELATION_TYPE } from "matrix-js-sdk/src/models/thread";
import { type MatrixEvent } from "matrix-js-sdk/src/matrix";

type ThreadWithInternals = Thread & {
    _currentUserParticipated?: boolean;
    client: { getUserId: () => string | null };
    rootEvent?: { getSender: () => string | null };
    events: MatrixEvent[];
};

type PatchedThreadPrototype = {
    __sshThreadRelevancePatchApplied?: boolean;
    addEvent: (event: MatrixEvent, toStartOfTimeline: boolean, emit?: boolean) => void;
    isRelevantToUser: () => boolean;
};

/**
 * Keeps thread relevance accurate for users who replied in a thread but are not its root author.
 * Without this, some threads can stop appearing relevant after sync/update races.
 */
export const patchThreadRelevanceForParticipation = (): void => {
    const proto = Thread.prototype as ThreadWithInternals & PatchedThreadPrototype;
    if (proto.__sshThreadRelevancePatchApplied) return;
    proto.__sshThreadRelevancePatchApplied = true;

    const originalAddEvent = proto.addEvent;
    proto.addEvent = function (
        this: ThreadWithInternals,
        event: MatrixEvent,
        toStartOfTimeline: boolean,
        emit = true,
    ): void {
        const userId = this.client.getUserId();
        if (
            !!userId &&
            event.getId() !== this.id &&
            event.isRelation(THREAD_RELATION_TYPE.name) &&
            event.getSender() === userId
        ) {
            this._currentUserParticipated = true;
        }

        originalAddEvent.call(this, event, toStartOfTimeline, emit);
    };

    const originalIsRelevantToUser = proto.isRelevantToUser;
    proto.isRelevantToUser = function (this: ThreadWithInternals): boolean {
        if (originalIsRelevantToUser.call(this)) return true;

        const userId = this.client.getUserId();
        if (!userId) return false;

        const isUserRootAuthor = this.rootEvent?.getSender() === userId;
        if (isUserRootAuthor) return true;

        // Fallback for cases where current_user_participated in bundled relations is stale.
        return this.events.some(
            (event) =>
                event.getId() !== this.id &&
                event.isRelation(THREAD_RELATION_TYPE.name) &&
                event.getSender() === userId,
        );
    };
};
